import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Trash2 } from "react-feather";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";
import { listTdsSlabsApi, saveTdsSlabsApi } from "../../../../Api/payrollApi";
import { DEFAULT_TDS_SLABS, PAYROLL_STRINGS } from "../../../../Constant/HR/payrollConstants";
import { checkStatusCodeSuccess, rendorBackButton } from "../../../../Constant/common";
import "./TaxSlabSettings.css";

let tempIdCounter = 1;
const genTempId = () => `tds_temp_${tempIdCounter++}`;

const TdsSlabConfig = () => {
  const navigate = useNavigate();
  const [slabs, setSlabs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Declare fetchSlabs before useEffect so it is not accessed before declaration
  const fetchSlabs = useCallback(() => {
    setIsLoading(true);
    listTdsSlabsApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const data = resp?.data ?? [];
          if (data.length === 0) {
            // Pre-populate with default TDS slabs when API returns empty
            setSlabs(
              DEFAULT_TDS_SLABS.map((s) => ({
                ...s,
                _id: genTempId(),
              })),
            );
          } else {
            setSlabs(
              data.map((s) => ({
                ...s,
                _id: s.id ?? genTempId(),
              })),
            );
          }
        }
      })
      .catch((err) => err)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchSlabs(); // eslint-disable-line react-hooks/set-state-in-effect
  }, [fetchSlabs]);

  const handleAddRow = () => {
    setSlabs((prev) => [
      ...prev,
      {
        _id: genTempId(),
        incomeFrom: "",
        incomeTo: "",
        taxRatePercent: "",
      },
    ]);
  };

  const handleChange = (id, field, value) => {
    setSlabs((prev) => prev.map((s) => (s._id === id ? { ...s, [field]: value } : s)));
  };

  const handleDelete = (id) => {
    setSlabs((prev) => prev.filter((s) => s._id !== id));
  };

  const handleSave = () => {
    // eslint-disable-next-line no-unused-vars
    const payload = slabs.map(({ _id, ...rest }, idx) => ({
      ...rest,
      incomeFrom: rest.incomeFrom === "" ? null : Number(rest.incomeFrom),
      // Last slab must always have incomeTo: null (no upper bound)
      incomeTo:
        idx === slabs.length - 1
          ? null
          : rest.incomeTo === "" || rest.incomeTo == null
            ? null
            : Number(rest.incomeTo),
      taxRatePercent: rest.taxRatePercent === "" ? null : Number(rest.taxRatePercent),
    }));

    setIsSaving(true);
    saveTdsSlabsApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(PAYROLL_STRINGS.SUCCESS_SLABS_SAVED);
          fetchSlabs();
        } else {
          toast.error(PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch(() => toast.error(PAYROLL_STRINGS.ERROR_GENERIC))
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="animate-fade-in d-flex flex-column gap-3">
      {/* Page header */}
      <div className="animate-slide-down d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="page-header-title">{PAYROLL_STRINGS.SETTINGS_TDS_TITLE}</h1>
          <p className="page-header-subtitle">{PAYROLL_STRINGS.SETTINGS_TDS_SUBTITLE}</p>
        </div>
        {rendorBackButton(() => navigate("/payrollTemplates"))}
      </div>

      {/* Content card */}
      <div className="glass rounded-3 p-5 animate-slide-up">
        {/* Toolbar */}
        <div className="d-flex justify-content-end gap-2 mb-4">
          <BaseButton
            label={PAYROLL_STRINGS.BTN_ADD_ROW}
            color="secondary"
            onClick={handleAddRow}
            disabled={isLoading}
          />
          <BaseButton
            label={isSaving ? PAYROLL_STRINGS.LOADING_SAVING : PAYROLL_STRINGS.BTN_SAVE_SLABS}
            color="primary"
            loader={isSaving}
            onClick={handleSave}
            disabled={isLoading || isSaving}
          />
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-hover" style={{ width: "100%" }}>
            <thead className="border-bottom" style={{ background: "var(--glass-bg)" }}>
              <tr>
                {["Income From (₹)", "Income To (₹)", "Tax Rate (%)", ""].map((col) => (
                  <th
                    key={col}
                    className="p-3 fs-8 fw-bold text-uppercase text-start"
                    style={{
                      color: "var(--text-primary)",
                      letterSpacing: "0.08em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="p-5 text-center"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : slabs.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="p-5 text-center"
                    style={{ color: "var(--text-primary)" }}
                  >
                    No slabs configured. Click &quot;Add Row&quot; to begin.
                  </td>
                </tr>
              ) : (
                slabs.map((slab) => (
                  <tr key={slab._id} className="border-bottom">
                    {/* Income From */}
                    <td className="p-3">
                      <input
                        type="number"
                        aria-label="Income From"
                        value={slab.incomeFrom ?? ""}
                        min={0}
                        onChange={(e) => handleChange(slab._id, "incomeFrom", e.target.value)}
                        className="form-control form-control-sm"
                        style={{
                          minWidth: 140,
                          borderRadius: 8,
                          border: "1px solid var(--glass-border)",
                          background: "var(--glass-bg)",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                        }}
                      />
                    </td>

                    {/* Income To */}
                    <td className="p-3">
                      <input
                        type="number"
                        aria-label="Income To"
                        placeholder="No limit"
                        value={slab.incomeTo ?? ""}
                        min={0}
                        onChange={(e) => handleChange(slab._id, "incomeTo", e.target.value)}
                        className="form-control form-control-sm"
                        style={{
                          minWidth: 140,
                          borderRadius: 8,
                          border: "1px solid var(--glass-border)",
                          background: "var(--glass-bg)",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                        }}
                      />
                    </td>

                    {/* Tax Rate */}
                    <td className="p-3">
                      <input
                        type="number"
                        aria-label="Tax Rate Percent"
                        value={slab.taxRatePercent ?? ""}
                        min={0}
                        max={100}
                        onChange={(e) => handleChange(slab._id, "taxRatePercent", e.target.value)}
                        className="form-control form-control-sm"
                        style={{
                          minWidth: 120,
                          borderRadius: 8,
                          border: "1px solid var(--glass-border)",
                          background: "var(--glass-bg)",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                        }}
                      />
                    </td>

                    {/* Delete */}
                    <td className="p-3 text-center">
                      <BaseTooltip label="Delete This Tax Slab" color="#be123c">
                        <BaseButton
                          icon={<Trash2 size={14} strokeWidth={2} />}
                          onClick={() => handleDelete(slab._id)}
                          aria-label="Delete row"
                          color="light"
                          className="slab-delete-btn"
                        />
                      </BaseTooltip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TdsSlabConfig;
